import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

const PANEL_WIDTH = 360;
const PANEL_MAX_HEIGHT = 420;

function PreviewField({ title, value, multiline = false, tone = "" }) {
  if (!value) return null;
  return (
    <div
      className={`gvisit-comm-suggestion__block${
        tone ? ` gvisit-comm-suggestion__block--${tone}` : ""
      } schedule-preview-comm-hover__block`}
    >
      <div className="gvisit-comm-suggestion__block-head">
        <span className="gvisit-comm-suggestion__block-title">{title}</span>
      </div>
      <div
        className={`gvisit-comm-suggestion__preview${
          multiline ? " gvisit-comm-suggestion__preview--multiline" : ""
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function TemplatePreview({ block }) {
  return (
    <section className="schedule-preview-comm-hover__template">
      <h4 className="schedule-preview-comm-hover__template-title">{block.typeLabel}</h4>
      {block.isEmail || block.isSms ? (
        <div className="gvisit-comm-suggestion__block schedule-preview-comm-hover__block schedule-preview-comm-hover__block--channels">
          <div className="gvisit-comm-suggestion__block-head">
            <span className="gvisit-comm-suggestion__block-title">Channels</span>
          </div>
          <div className="gvisit-comm-suggestion__preview">
            {[block.isEmail && "Email", block.isSms && "SMS"].filter(Boolean).join(" + ")}
          </div>
        </div>
      ) : null}
      {block.isEmail ? (
        <>
          <PreviewField title="Email subject" value={block.subject || "—"} tone="subject" />
          <PreviewField
            title="Email body"
            value={block.body || "—"}
            multiline
            tone="email"
          />
        </>
      ) : null}
      {block.isSms ? (
        <PreviewField title="SMS text" value={block.sms || "—"} multiline tone="sms" />
      ) : null}
    </section>
  );
}

function computePanelStyle(anchorRect) {
  if (!anchorRect) return null;
  const margin = 8;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  let left = anchorRect.left;
  let top = anchorRect.bottom + 6;
  if (left + PANEL_WIDTH > viewportWidth - margin) {
    left = Math.max(margin, anchorRect.right - PANEL_WIDTH);
  }
  if (top + PANEL_MAX_HEIGHT > viewportHeight - margin) {
    top = Math.max(margin, anchorRect.top - PANEL_MAX_HEIGHT - 6);
  }
  top = Math.max(margin, Math.min(top, viewportHeight - PANEL_MAX_HEIGHT - margin));
  return {
    position: "fixed",
    top: `${top}px`,
    left: `${left}px`,
    width: `${PANEL_WIDTH}px`,
    maxHeight: `${PANEL_MAX_HEIGHT}px`,
    zIndex: 12000,
  };
}

export default function SchedulePreviewCommHover({ summary, templates = [], isDetail = false }) {
  const popoverId = useId();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState(null);

  const updatePanelPosition = useCallback(() => {
    setPanelStyle(computePanelStyle(rootRef.current?.getBoundingClientRect()));
  }, []);

  const handleOpen = useCallback(() => {
    updatePanelPosition();
    setOpen(true);
  }, [updatePanelPosition]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setPanelStyle(null);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onReposition = () => updatePanelPosition();
    window.addEventListener("scroll", onReposition, true);
    window.addEventListener("resize", onReposition);
    return () => {
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("resize", onReposition);
    };
  }, [open, updatePanelPosition]);

  if (!templates.length) {
    return (
      <span
        className={`schedule-preview-check__message${isDetail ? " is-detail" : ""}`}
      >
        {summary}
      </span>
    );
  }

  const popover =
    open && panelStyle ? (
      <div
        id={popoverId}
        className="schedule-preview-comm-hover__panel gvisit-comm-suggestion__panel"
        style={panelStyle}
        role="dialog"
        aria-label="Communication templates"
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
      >
        <p className="gvisit-comm-suggestion__intro">
          Full <strong>communication templates</strong> saved for this visit.
        </p>
        {templates.map((block) => (
          <TemplatePreview key={block.typeLabel} block={block} />
        ))}
      </div>
    ) : null;

  return (
    <span
      ref={rootRef}
      className={`schedule-preview-comm-hover${open ? " is-open" : ""}`}
      onMouseEnter={handleOpen}
      onMouseLeave={handleClose}
    >
      <span
        className={`schedule-preview-check__message schedule-preview-comm-hover__trigger${
          isDetail ? " is-detail" : ""
        }`}
      >
        {summary}
        <i className="fas fa-eye schedule-preview-comm-hover__icon" aria-hidden />
        <span className="schedule-preview-comm-hover__hint">Hover to view full templates</span>
      </span>
      {typeof document !== "undefined" && popover ? createPortal(popover, document.body) : null}
    </span>
  );
}
